import React, { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Combobox, Transition } from '@headlessui/react';
import { Search, CornerDownLeft, type LucideIcon } from 'lucide-react';
import { resolveNavIcon } from './iconMap';

export interface PaletteItem {
  path: string;
  name: string;
  section: string;
  icon?: string;
  iconComponent?: LucideIcon;
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: PaletteItem[];
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, items }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q) ||
        item.keywords?.some((kw) => kw.toLowerCase().includes(q))
    );
  }, [items, query]);

  const grouped = useMemo(() => {
    const groups = new Map<string, PaletteItem[]>();
    for (const item of filtered) {
      const list = groups.get(item.section) || [];
      list.push(item);
      groups.set(item.section, list);
    }
    return Array.from(groups.entries());
  }, [filtered]);

  const handleSelect = (item: PaletteItem | null) => {
    if (!item) return;
    onClose();
    setQuery('');
    navigate(item.path);
  };

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-start justify-center p-4 pt-[12vh]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-[0.98] -translate-y-2"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-[0.98]"
          >
            <Dialog.Panel className="w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface shadow-palette">
              <Combobox onChange={handleSelect} nullable>
                <div className="flex items-center gap-3 border-b border-line px-4">
                  <Search className="h-4 w-4 shrink-0 text-ink-subtle" />
                  <Combobox.Input
                    autoFocus
                    className="h-13 w-full bg-transparent py-4 text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
                    placeholder="Search modules, pages, settings..."
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <kbd className="hidden rounded-md border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] font-medium text-ink-subtle sm:block">
                    ESC
                  </kbd>
                </div>

                <Combobox.Options static className="max-h-[50vh] overflow-y-auto p-2">
                  {grouped.length === 0 && (
                    <div className="px-4 py-10 text-center text-sm text-ink-muted">
                      No results for "{query}"
                    </div>
                  )}
                  {grouped.map(([section, sectionItems]) => (
                    <div key={section} className="mb-1">
                      <div className="px-3 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-subtle">
                        {section}
                      </div>
                      {sectionItems.map((item) => {
                        const Icon: LucideIcon = item.iconComponent ?? resolveNavIcon(item.path, item.icon);
                        return (
                          <Combobox.Option key={`${section}-${item.path}`} value={item} as={Fragment}>
                            {({ active }) => (
                              <div
                                className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                  active ? 'bg-accent text-accent-fg' : 'text-ink'
                                }`}
                              >
                                <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-accent-fg' : 'text-ink-subtle'}`} />
                                <span className="flex-1 truncate font-medium">{item.name}</span>
                                <span className={`truncate font-mono text-[10px] ${active ? 'text-accent-fg/70' : 'text-ink-subtle'}`}>
                                  {item.path}
                                </span>
                                {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-accent-fg/80" />}
                              </div>
                            )}
                          </Combobox.Option>
                        );
                      })}
                    </div>
                  ))}
                </Combobox.Options>

                <div className="flex items-center gap-4 border-t border-line bg-surface-2 px-4 py-2.5 font-mono text-[10px] text-ink-subtle">
                  <span className="flex items-center gap-1.5">
                    <kbd className="rounded border border-line bg-surface px-1 py-0.5">↑↓</kbd> navigate
                  </span>
                  <span className="flex items-center gap-1.5">
                    <kbd className="rounded border border-line bg-surface px-1 py-0.5">↵</kbd> open
                  </span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
